import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { FormikConfig, FormikHelpers } from 'formik';
import { createContext, useContext } from 'use-context-selector';
import * as Yup from 'yup';

import { FORGOT_PASSWORD_EMAIL_SENT_PATH } from '~configs';
import { useForgotPasswordMutation } from '~hooks';

export type ForgotPasswordValues = {
  email: string;
};

export const forgotPasswordValidationSchema = Yup.object({
  email: Yup.string().max(255).required('Email is required'),
});

const initialContextValues = {
  forgotPasswordValues: {} as ForgotPasswordValues,
  handleSubmit: (
    _: ForgotPasswordValues,
    _formikHelpers: FormikHelpers<ForgotPasswordValues>,
  ) => {
    return;
  },
  isRequesting: false,
  requestedEmail: '',
};

const ForgotPasswordContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const ForgotPasswordProvider: React.FC<Props> = ({ children }) => {
  // Initial data
  const initialForgotPasswordValues = useMemo(
    () => ({
      email: '',
    }),
    [],
  );

  const navigate = useNavigate();
  const currentURL = window.location.hostname;
  const [requestedEmail, setRequestedEmail] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);

  const forgotPassword = useForgotPasswordMutation();

  const handleSubmit: FormikConfig<ForgotPasswordValues>['onSubmit'] =
    useCallback(
      (values, helpers) => {
        setIsRequesting(true);
        forgotPassword.mutate(
          {
            email: values.email,
            redirectUrl: `${currentURL}/reset-password/`,
          },
          {
            onError: (err) => {
              if (err instanceof Yup.ValidationError || isAxiosError(err)) {
                helpers.setStatus({ success: false });
                helpers.setErrors({
                  email: `Email doesn't match any account. Please try again or contact chargeFUZE Support.`,
                });
                helpers.setSubmitting(false);
              }
            },
            onSuccess: () => {
              setRequestedEmail(values.email);
              navigate(FORGOT_PASSWORD_EMAIL_SENT_PATH);
            },
            onSettled: () => {
              setIsRequesting(false);
            },
          },
        );
      },
      [currentURL, forgotPassword, navigate],
    );

  const providerValues = useMemo(
    () => ({
      forgotPasswordValues: initialForgotPasswordValues,
      handleSubmit,
      isRequesting,
      requestedEmail,
    }),
    [handleSubmit, initialForgotPasswordValues, isRequesting, requestedEmail],
  );

  return (
    <ForgotPasswordContext.Provider value={providerValues}>
      {children}
    </ForgotPasswordContext.Provider>
  );
};

export const useForgotPasswordContext = () => useContext(ForgotPasswordContext);
